import bgImage from '@assets/auth-bg.png';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { AuthGradIcon } from '@components/ui/atoms/Icons';
import { AuthQuoteCard } from '@components/ui/molecules/AuthQuoteCard';
import { BrandFooter } from '@components/ui/molecules/BrandFooter';
import {
  floatHover8,
  floatHover8Reverse,
  floatHover12Reverse,
  slideUpFade,
} from '@shared/utils/animationsUtil';

export const AuthLayout: React.FCWithChildren = (props) => {
  return (
    <Flex
      w="full"
      h="full"
      as="main"
      position="relative"
      overflow="hidden"
      flexDirection={{ base: 'column', lg: 'row' }}
      css={{
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
        },
      }}
    >
      <Flex
        display={{ base: 'none', lg: 'flex' }}
        flex="1"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex="1"
        color="title.dark"
        overflowX="hidden"
        overflowY="auto"
      >
        <Box position="relative">
          {/* Top-left icon — overlaps top-left corner of card */}
          <Box
            position="absolute"
            top={{ lg: '-65px', xl: '-70px', '2xl': '-120px' }}
            left={{ lg: '-35px', xl: '-65px', '2xl': '-130px' }}
            opacity={0}
            animation={`${slideUpFade} 0.6s ease-out forwards, ${floatHover8Reverse} 8s ease-in-out infinite 0.6s`}
          >
            <AuthGradIcon
              width={{ lg: '120px', xl: '134px', '2xl': '184px' }}
              height={{ lg: '120px', xl: '134px', '2xl': '184px' }}
            />
          </Box>

          {/* Bottom-right icon — largest, overlaps bottom-right corner of card */}
          <Box
            position="absolute"
            bottom={{ lg: '-75px', xl: '-80px', '2xl': '-130px' }}
            right={{ lg: '-25px', xl: '-75px', '2xl': '-130px' }}
            opacity={0}
            animation={`${slideUpFade} 0.8s ease-out forwards 0.2s, ${floatHover8} 9s ease-in-out infinite 1s`}
          >
            <AuthGradIcon
              width={{ lg: '130px', xl: '180px', '2xl': '240px' }}
              height={{ lg: '130px', xl: '180px', '2xl': '240px' }}
              transform="rotate(-45deg)"
            />
          </Box>

          {/* Bottom-left icon — rotated upside down, near bottom-left of card */}
          <Box
            position="absolute"
            bottom={{ lg: '-65px', xl: '-90px', '2xl': '-160px' }}
            left={{ lg: '-45px', xl: '-55px', '2xl': '-110px' }}
            opacity={0}
            animation={`${slideUpFade} 0.7s ease-out forwards 0.1s, ${floatHover12Reverse} 4.5s ease-in-out infinite 0.8s`}
          >
            <AuthGradIcon
              width={{ lg: '50px', xl: '65px', '2xl': '75px' }}
              height={{ lg: '50px', xl: '65px', '2xl': '75px' }}
              transform="rotate(-120deg)"
            />
          </Box>

          <AuthQuoteCard />
        </Box>
        <BrandFooter />
      </Flex>
      <Flex
        display="flex"
        bg="body.50"
        flex="1"
        minHeight={0}
        width={{ base: 'full', lg: 'auto' }}
        height="full"
        alignItems="flex-start"
        justifyContent="center"
        position="relative"
        zIndex="1"
        overflowY="auto"
        overflowX="hidden"
        css={{
          WebkitOverflowScrolling: 'touch',
          /* Android Chrome fix: ensure proper scrolling */
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="full"
          minHeight="full"
          padding={{
            base: '48px 24px',
            sm: '64px 48px',
            md: '72px 80px',
            lg: '72px 0',
            xl: '72px 0',
          }}
          css={{
            '@media (max-width: 639px)': {
              paddingBottom: '80px !important',
            },
            '@media (min-width: 640px) and (max-width: 767px)': {
              paddingBottom: '96px !important',
            },
            '@media (min-width: 768px) and (max-width: 1023px)': {
              paddingBottom: '88px !important',
            },
          }}
        >
          {props.children}
        </Box>
      </Flex>
    </Flex>
  );
};
